import type {
  ArtistPortalDimensionTotal,
  ArtistPortalEarningsResponse,
  ArtistPortalStreamsByDimensionResponse,
} from '../smartlinks/artistPortals';

/**
 * The wire contract of the Streaming screen ("Your intelligence" → Streaming), between
 * music24-api and the web and native apps. Streaming replaces Insights.
 *
 * Design: docs/superpowers/design-reference/music24-your-intelligence/ (frames a1, b1, c to f).
 *
 * Rules of the numbers (design §4.2):
 *   - One source of truth for each number. With a distributor connected (Ditto Music), Streams is
 *     the distributor total for all stores. The portal streams are never added to it, because the
 *     distributor already counts them. With no distributor, Streams is the sum over the portals.
 *   - Listeners is one sum over Spotify for Artists and Deezer for Creators (owner answer
 *     2026-10-06). Followers is Spotify and Deezer, Subscribers is YouTube.
 *   - A number that no connected source gives is null, never 0.
 *   - The change compares with the period of the same length directly before.
 *   - Money has a currency code on every amount. Amounts of two currencies are never added.
 *   - A caller who may not see money (a member when "Members can see earnings" is off, an OAuth
 *     token) gets `earnings.hidden: true` and no amount anywhere in the answer.
 *
 * Every connected source is a source of the same kinds of data (owner decision 2026-10-06). A
 * direct portal (Spotify for Artists, Deezer for Creators, YouTube) is one store. A distributor
 * (Ditto Music) reports many stores at once. A section (Stores, Countries, Earnings) is available
 * when at least one connected source gives its data (`StreamingOverviewResponse.sections`). Nothing
 * is locked only because no distributor is connected.
 *
 * No double count in a section (the rule of Streams, applied per store and per country):
 *   - A distributor wins for each store and each country that it reports: its number already holds
 *     the streams that a direct portal sees for its releases.
 *   - A direct portal adds its own store row only when no distributor reports that store
 *     (`Spotify for Artists` → store "Spotify", `Deezer for Creators` → "Deezer", `YouTube` →
 *     "YouTube"; a distributor store whose name starts with the same word is the same store).
 *   - A country of a direct portal is added only when no distributor reports that country. Two
 *     direct portals add up per country: they count different stores.
 */

/** The periods the picker offers, in days. The server cuts a period to `historyDaysLimit`. */
export const STREAMING_DAY_OPTIONS = [7, 28, 90, 180, 365] as const;
export type StreamingDays = (typeof STREAMING_DAY_OPTIONS)[number];

/** The period the screen opens on. The Free plan opens on 7 days. */
export const STREAMING_DEFAULT_DAYS: StreamingDays = 28;

/**
 * The earnings change against the previous period is reliable only for a period of at least this
 * many whole months (owner answer 2026-10-06: the newest weeks can still grow).
 */
export const STREAMING_EARNINGS_RELIABLE_MONTHS = 3;

/**
 * The months that can still grow: a store reports late, so the amounts of the newest months are
 * not final. The note shows when the period holds one of them.
 */
export const STREAMING_EARNINGS_LATE_MONTHS = 2;

export type StreamingSourceKind = 'portal' | 'distributor';

/**
 * The sections past the totals that a source can give. The last four (2026-10-16) are the private
 * period numbers of `streamingSections.ts`: Audience, Play sources, Short video, Accelerator.
 * Shipped apps keep only the keys they know (iOS `compactMap`, Android `ignoreUnknownKeys`).
 */
export type StreamingSectionKey =
  | 'stores'
  | 'countries'
  | 'earnings'
  | 'audience'
  | 'playSources'
  | 'shortVideo'
  | 'accelerator';

export const STREAMING_SECTION_KEYS: readonly StreamingSectionKey[] = [
  'stores',
  'countries',
  'earnings',
  'audience',
  'playSources',
  'shortVideo',
  'accelerator',
];

/** Who gives the data of one section. */
export interface StreamingSectionSources {
  /**
   * The connected sources (`integrationType`) that give the data of this section: a source with an
   * artist, also a disconnected one whose numbers stay as history. Empty: the section is empty.
   */
  sources: string[];
  /**
   * Every kind of source that Music24 can connect and that gives this data, connected or not
   * (`integrationType`). The empty section names them ("Sources that report earnings: Ditto").
   */
  givenBy: string[];
  /**
   * `'plan'` when the plan of the workspace does not show this section (Free: the split by store
   * and country, and the earnings). Absent when the plan shows it.
   */
  locked?: 'plan';
}

/**
 * Why an answer holds no money: `'plan'` when the plan of the workspace has no earnings (Free),
 * `'role'` when the caller may not see them (a member without the owner's switch, an OAuth token).
 */
export type EarningsHiddenReason = 'plan' | 'role';

/** The numbers of the totals row and of the second row (design b1). */
export type StreamingTotalKey =
  | 'streams'
  | 'listeners'
  | 'followers'
  | 'subscribers'
  | 'saves'
  | 'playlistAdds'
  | 'monthlyListeners'
  | 'downloads';

export const STREAMING_TOTAL_KEYS: readonly StreamingTotalKey[] = [
  'streams',
  'listeners',
  'followers',
  'subscribers',
  'saves',
  'playlistAdds',
  'monthlyListeners',
  'downloads',
];

/** The state of a source, as the source pill and the source card draw it (design f1). */
export type StreamingSourceState =
  /** Read today, numbers there. */
  | 'ok'
  /** The first read runs: no number yet. */
  | 'firstRead'
  /** The sign-in ended. The numbers stop at `stoppedAt`. */
  | 'reconnect'
  /** The last read failed. Music24 tries again tonight. */
  | 'failed'
  /** Signed in, but no artist is chosen yet. */
  | 'noArtist'
  /**
   * The artist disconnected the source. The numbers already read stay and show as history up to
   * `stoppedAt`; no new read runs (owner decision 2026-10-06).
   */
  | 'disconnected';

export interface StreamingSource {
  /** `spotify`, `deezer`, `youtubeMusic` or `ditto`. */
  integrationType: string;
  kind: StreamingSourceKind;
  state: StreamingSourceState;
  artistName: string | null;
  /** ISO datetime of the last read, or null. */
  lastSyncAt: string | null;
  /** YYYY-MM-DD: the newest day with numbers, or null. */
  syncedThrough: string | null;
  /** `reconnect` and `disconnected` only: the day the numbers stop (YYYY-MM-DD). Else null. */
  stoppedAt: string | null;
  /** `disconnected` only: ISO datetime of the Disconnect. Else null. */
  disconnectedAt: string | null;
  /** The totals this source gives. */
  gives: StreamingTotalKey[];
  /**
   * The sections this source gives. A direct portal gives one store (its own); a distributor gives
   * stores, countries and earnings. Absent on a server from before 2026-10-06.
   */
  sections?: StreamingSectionKey[];
}

export interface StreamingTotal {
  /** Null when no connected source gives this number. */
  current: number | null;
  previous: number | null;
  /** Percent. Null when it cannot be stated (no previous number, or a previous 0). */
  changePct: number | null;
  /** The sources that gave the number, `integrationType` values. Empty when `current` is null. */
  sources: string[];
}

/** One day of one source. Only the numbers the source gives on that day are there. */
export interface StreamingSeriesPoint {
  /** YYYY-MM-DD */
  date: string;
  streams?: number;
  listeners?: number;
  saves?: number;
  playlistAdds?: number;
  downloads?: number;
}

export interface StreamingSourceSeries {
  integrationType: string;
  points: StreamingSeriesPoint[];
}

/** The money of one currency. */
export interface StreamingMoney {
  /** ISO 4217, or null when the source did not say. Never assume USD. */
  currency: string | null;
  net: number | null;
  gross: number | null;
}

/** The net and gross of one currency in the period, with the previous period of that currency. */
export interface StreamingEarningsAmount extends StreamingMoney {
  previousNet: number | null;
  /** Null when the change is not reliable (`changeReliable: false`) or has no previous number. */
  changePct: number | null;
}

export interface StreamingEarningsSummary {
  /** False when this deploy has no private-metrics store. */
  available: boolean;
  /**
   * True when a source that reports earnings is connected and has an artist (today only a
   * distributor such as Ditto reports earnings).
   */
  connected: boolean;
  /**
   * The connected sources whose amounts these are (`integrationType`), for "As reported by …".
   * Empty when `connected` is false. Absent on a server from before 2026-10-06 (then: Ditto).
   */
  sources?: string[];
  /**
   * True when the caller may not see money. Every amount of the whole answer is then absent:
   * `amounts` is null and the track rows have no `earnings`.
   */
  hidden: boolean;
  /** Why `hidden` is true. Absent when `hidden` is false and on an old server. */
  hiddenReason?: EarningsHiddenReason;
  /** The months the amounts cover (`YYYY-MM`), or null when no whole month is inside the plan window. */
  months: { from: string; to: string } | null;
  /**
   * One entry for each currency, the currency of the newest amounts first. Empty when nothing is
   * reported. Null when `hidden`. Two currencies are never added.
   */
  amounts: StreamingEarningsAmount[] | null;
  /** False for a period shorter than `STREAMING_EARNINGS_RELIABLE_MONTHS`: the change is not shown. */
  changeReliable: boolean;
  /** True when the months hold one of the newest months, whose amounts can still grow. */
  mayGrow: boolean;
}

/**
 * GET /accounts/:accountId/streaming/overview?days=7|28|90|180|365
 */
export interface StreamingOverviewResponse {
  /** The days the server served: the asked days cut to the plan. */
  days: number;
  /** The history the plan shows (`historyDaysLimit`). */
  historyDaysLimit: number;
  /** True when the asked days were more than the plan shows. */
  limited: boolean;
  range: { from: string; to: string };
  previousRange: { from: string; to: string } | null;
  /**
   * True when a distributor is connected with an artist: Streams then comes from it alone. It no
   * longer decides what the screen shows; `sections` does. Kept for shipped apps.
   */
  hasDistributor: boolean;
  /**
   * Who gives each section. A section with no source is empty, with a neutral line. Absent on a
   * server from before 2026-10-06: then read `hasDistributor` as before.
   */
  sections?: Record<StreamingSectionKey, StreamingSectionSources>;
  sources: StreamingSource[];
  totals: Record<StreamingTotalKey, StreamingTotal>;
  series: StreamingSourceSeries[];
  earnings: StreamingEarningsSummary;
}

/** The numbers of one source for one track. */
export interface StreamingTrackSourceNumbers {
  integrationType: string;
  externalId: string;
  streams: number | null;
  listeners: number | null;
  saves: number | null;
}

export interface StreamingTrackRow {
  /** A stable key: `catalog:<uuid>`, else `isrc:<isrc>`, else `name:<name>`. */
  key: string;
  name: string;
  imageUrl: string | null;
  catalogItemUuid: string | null;
  releaseDate: string | null;
  isrc: string | null;
  barcode: string | null;
  /** The distributor track id, when the distributor has the track. For the detail reads. */
  distributorTrackId: string | null;
  streams: number | null;
  previousStreams: number | null;
  changePct: number | null;
  /** Ditto's unique listeners of the last 28 days when a distributor has the track, else the portal sum. */
  listeners: number | null;
  saves: number | null;
  /** Absent when the caller may not see money. One entry per currency, never added. */
  earnings?: StreamingMoney[];
  bySource: StreamingTrackSourceNumbers[];
}

/**
 * GET /accounts/:accountId/streaming/tracks?days&limit
 */
export interface StreamingTracksResponse {
  days: number;
  historyDaysLimit: number;
  range: { from: string; to: string };
  hasDistributor: boolean;
  /** True when the caller may not see money: no row has `earnings`. */
  earningsHidden: boolean;
  /** Why `earningsHidden` is true. Absent when it is false and on an old server. */
  hiddenReason?: EarningsHiddenReason;
  /** Largest first, by streams. */
  tracks: StreamingTrackRow[];
}

export type StreamingDimension = 'store' | 'country';

/** One store or country of the merged list, with the source whose number it is. */
export interface StreamingDimensionRow extends ArtistPortalDimensionTotal {
  /** The `integrationType` of the source that gave this row (see the no-double-count rule). */
  source: string;
}

/**
 * GET /accounts/:accountId/streaming/stores?days=7|28|90|180|365 (or `from&to`)
 * GET /accounts/:accountId/streaming/countries (same query)
 *
 * The stores or countries of the artist from every connected source, merged by the
 * no-double-count rule at the top of this file. The answer has the shape of the per-source route
 * (`/artistPortals/:integrationType/streams/byStore`), so a client draws both the same way.
 * `trackExternalId` is always null: a track reads the per-source route.
 */
export interface StreamingDimensionResponse extends ArtistPortalStreamsByDimensionResponse {
  totals: StreamingDimensionRow[];
  /** The sources whose rows are in `totals`, `integrationType` values. */
  sources: string[];
}

/**
 * GET /accounts/:accountId/streaming/earnings?groupBy=store|track|period|country&from=YYYY-MM&to=YYYY-MM
 *
 * The earnings of every connected source that reports earnings. Amounts of two currencies are
 * never added. Rows with the same label and currency from two sources are added: each source
 * pays out for its own releases.
 */
export interface StreamingEarningsResponse extends ArtistPortalEarningsResponse {
  /** The sources whose amounts are in the answer, `integrationType` values. */
  sources: string[];
}
